import React, { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { format } from 'date-fns'
import mixpanel from 'mixpanel-browser'
import { DateRange, Range } from 'react-date-range'
import styled from 'styled-components'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import { useBuildingContext } from '../../../../contexts/buildingContext'
import { Text } from '../../../common/Text'

const DateRangeSelectorBase = styled.div`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  width: fit-content;
  margin: 0 auto;
  display: flex;
  align-items: center;
  flex-direction: column;
`

const DateRangeSelectorPopup = styled.div`
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
`

const DateRangeSelectorDisplay = styled.div`
  width: 246px;
  height: 60px;

  background: #ffffff;

  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  border-radius: 2px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  user-select: none;

  &.inverted {
    background: #073c7a;
  }
`

const DRClear = styled.div`
  position: absolute;
  top: -30px;
  right: 0;
  font-size: 0.8em;
  background: #fff;
  border-radius: 10px;
  color: #073c7a;
  padding: 5px;
`

export interface DateRangeSelectorProps {
  min?: Date
  max?: Date
}

export const DateRangeSelector = (props: DateRangeSelectorProps) => {
  const { min, max } = props
  const { state: buildingState, updateBuilding } = useBuildingContext()
  const selectorRef = useRef() as React.MutableRefObject<HTMLInputElement>

  // If min/max are set, use them for initial selection and restrict selection
  const initialStart = min ? min : new Date()
  const initialEnd = max ? max : new Date()
  const selectionRange: Range = {
    startDate: initialStart,
    endDate: initialEnd,
    key: 'selection',
  }

  const [tempRange, setTempRange] = useState<Range>(selectionRange)
  const [selectedRange, setSelectedRange] = useState<Range | null>(null)
  const [displayed, setDisplayed] = useState(false)

  useEffect(() => {
    // Only update if min/max are set and the current range is different
    if (min && max) {
      const isTempRangeEqual =
        tempRange.startDate?.getTime() === min.getTime() &&
        tempRange.endDate?.getTime() === max.getTime()
      const isSelectedRangeEqual =
        selectedRange?.startDate?.getTime() === min.getTime() &&
        selectedRange?.endDate?.getTime() === max.getTime()
      const isBuildingRangeEqual =
        buildingState.dateRange?.startDate?.getTime() === min.getTime() &&
        buildingState.dateRange?.endDate?.getTime() === max.getTime()
      if (!isTempRangeEqual)
        setTempRange({ startDate: min, endDate: max, key: 'selection' })
      if (!isSelectedRangeEqual)
        setSelectedRange({ startDate: min, endDate: max, key: 'selection' })
      if (!isBuildingRangeEqual) {
        updateBuilding({
          dateRange: { startDate: min, endDate: max, key: 'selection' },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  const onClick = (e: any) => {
    if (displayed) {
      setDisplayed(false)
      setSelectedRange(tempRange)
      updateBuilding({ dateRange: tempRange })
      mixpanel.track('Set DateRange', {
        range: tempRange,
      })
    } else if (e.target.id !== 'clear') {
      if (selectedRange != null) setTempRange(selectedRange)
      setDisplayed(true)
    }
  }

  const clearRange = () => {
    setTempRange(selectionRange)
    setSelectedRange(null)
    updateBuilding({ dateRange: null })
  }

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
    function handleClickOutside(event: any) {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setDisplayed(false)
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [selectorRef])

  return (
    <DateRangeSelectorBase ref={selectorRef} id="drsb">
      {displayed && (
        <DateRangeSelectorPopup>
          <DateRange
            rangeColors={['#073c7a']}
            color="#073c7a"
            ranges={[tempRange]}
            minDate={min}
            maxDate={max || new Date()}
            showDateDisplay={false}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper item type
            onChange={(item: any) => {
              item.selection.endDate.setHours(23, 59, 59, 999)
              setTempRange(item.selection)
            }}
          />
        </DateRangeSelectorPopup>
      )}
      <DateRangeSelectorDisplay
        className={classNames({
          inverted: displayed,
        })}
        onClick={onClick}
      >
        {/* Hide clear button if min and max are set */}
        {buildingState.dateRange && !displayed && !(min && max) && (
          <DRClear onClick={clearRange} id="clear">
            Clear
          </DRClear>
        )}
        {displayed ? (
          <Text size={16} color={'white'}>
            Apply
          </Text>
        ) : buildingState.dateRange ? (
          <Text size={14} color={'#073c7a'} bold={false}>
            {format(buildingState.dateRange.startDate!, 'MMM dd, yyyy')}
            <span
              style={{
                display: 'inline-block',
                width: '16px',
                height: '1px',
                backgroundColor: '#000',
                margin: '4px 3px',
              }}
            />
            {format(buildingState.dateRange.endDate!, 'MMM dd, yyyy')}
          </Text>
        ) : (
          <Text size={16} color={'#073C7A'}>
            Select Range
          </Text>
        )}
      </DateRangeSelectorDisplay>
    </DateRangeSelectorBase>
  )
}
